/**
 * Life Blueprint V2.9.9 — M02 ReferenceData · Controlled Batch 06
 *   DB_藏干_展开 (Sheet43, A1:F29) — COMPATIBILITY / DERIVED-VIEW LAYER
 *   over the accepted M02 Batch 02 DB_藏干 authoritative dataset,
 *   plus Sheet27 B82:E84 and B90 deterministic composition helpers.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * STATUS
 *   • DB_藏干_展开 dataset status = COMPATIBILITY_LAYER (no independent
 *     28-row store; single source of truth is Batch 02 DB_藏干).
 *   • Sheet27 B82:E84 evidence status = COMPLETE (formula family closed,
 *     all four branch precedents audited).
 *   • Sheet27 B90 evidence status = COMPLETE; TRUE output audited as
 *     "空亡" (Unicode [31354, 20129]).
 *   • M02 overall: IN_PROGRESS, referenceCoverage = PARTIAL.
 *
 * DATA EQUIVALENCE EVIDENCE (recorded by audit fixture)
 *   Sheet42 DB_藏干!A2:F29 and Sheet43 DB_藏干_展开!A2:F29 were
 *   compared column-by-column using exact serialised values:
 *     A/B/C/D/E/F = exact match → 168/168 data cells identical.
 *   The ONLY differences are three header labels:
 *     A: 复合键(地支_类型)  →  复合键
 *     C: 类型               →  气类
 *     F: 权重(%)            →  权重
 *   Therefore this module MUST NOT introduce a second 28-row store.
 *   `DB_CANG_GAN_ZHAN_KAI.rows === DB_CANG_GAN.rows` — same array
 *   identity; both immutable.
 *
 * SHEET27 B82:E84 SEMANTICS (formula family)
 *   For each branch B37/B46/B54/B62, probe three composite keys
 *     (branch + "_" + 本气), (branch + "_" + 中气), (branch + "_" + 余气)
 *   over DB_藏干_展开 (== DB_藏干). Concatenate non-empty results with
 *   "/" (i.e., skip 气类 tokens that do not exist for that branch).
 *   Excel VLOOKUP returns "" when key is not found; the wrapping IFERROR
 *   ensures empty segments are skipped — so this is deterministic and
 *   NOT a fuzzy match. Return columns:
 *     B82:E82 → column 4 藏干
 *     B83:E83 → column 5 藏干五行
 *     B84:E84 → column 6 权重
 *   Weight is emitted as its native Excel Double integer stringified.
 *
 * SHEET27 B90 SEMANTICS
 *   Step 1: Compute 旬序号 band from B51 via the audited IF ladder
 *     (see M02 Batch05 `xunXuHaoBandFromB51`).
 *   Step 2: Look up (空亡支1, 空亡支2) in DB_空亡规则 for that band.
 *   Step 3: If B37 ∈ {空亡支1, 空亡支2} return "空亡" (audited Unicode
 *     [31354, 20129]) else "".
 *
 * ISOLATION
 *   Does NOT wire into `src/engine/referenceData.js` or the main
 *   V2.9.9 pipeline. Does NOT modify Batch 01/02/03/04/05 behaviour.
 *   Does NOT modify M03 Sheet27 Batch01–07 runtime.
 *
 * UNICODE POLICY
 *   Chinese literals built from audited AscW code points via
 *   `String.fromCodePoint(...)` (MIGRATION_RULES.md → Encoding Warning).
 *
 * FAIL-CLOSED / STRICT KEYS
 *   Direct authoritative lookups (`zhanKaiLookupByCompositeKey`,
 *   `zhanKaiLookupBy`) throw `MISSING_REFERENCE_KEY` for unknown keys,
 *   matching the M02 Batch 01/02/03/04/05 policy. `zhanKaiHasKey`
 *   provides a non-throwing existence probe used by the composition
 *   helpers (which must silently skip absent 中气/余气 per Excel
 *   VLOOKUP + IFERROR semantics).
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  DB_CANG_GAN,
  CANG_GAN_TYPE,
  cangGanLookup,
  cangGanLookupBy,
  cangGanRowsForDiZhi,
} from "@/engine/v299/m02_batch02_reference";
import {
  xunXuHaoBandFromB51,
  kongWangBranchesByXunXuHao,
} from "@/engine/v299/m02_batch05_reference";

// ── Constants ────────────────────────────────────────────────────────────

const KEY_SEP = "_";

// The three audited 气类 tokens (identical to Batch 02 's 类型 tokens —
// see DATA EQUIVALENCE EVIDENCE above). Re-exposed here under the
// Sheet43 header naming for reader convenience.
export const QI_LEI_TOKEN = Object.freeze({
  BEN_QI:   CANG_GAN_TYPE.BEN_QI,   // 本气 [26412, 27668]
  ZHONG_QI: CANG_GAN_TYPE.ZHONG_QI, // 中气 [20013, 27668]
  YU_QI:    CANG_GAN_TYPE.YU_QI,    // 余气 [20313, 27668]
});

// Sheet43 header labels (Unicode-authoritative — three differ from
// Sheet42 wording; see DATA EQUIVALENCE EVIDENCE above).
const HEADER_FU_HE_JIAN     = String.fromCodePoint(22797, 21512, 38190);              // 复合键
const HEADER_DI_ZHI         = String.fromCodePoint(22320, 25903);                     // 地支
const HEADER_QI_LEI         = String.fromCodePoint(27668, 31867);                     // 气类
const HEADER_CANG_GAN       = String.fromCodePoint(34255, 24178);                     // 藏干
const HEADER_CANG_GAN_WUX   = String.fromCodePoint(34255, 24178, 20116, 34892);       // 藏干五行
const HEADER_QUAN_ZHONG     = String.fromCodePoint(26435, 37325);                     // 权重

// Audited B90 TRUE output — recovered from Sheet27 B90 formula
// positions 260..261 (AscW unsigned 31354, 20129 → 空亡).
export const SHEET27_B90_TRUE_OUTPUT = String.fromCodePoint(31354, 20129); // 空亡
export const SHEET27_B90_TRUE_OUTPUT_UNICODE = Object.freeze([31354, 20129]);
export const SHEET27_B90_FALSE_OUTPUT = "";

// ── DB_藏干_展开 compatibility view (no row duplication) ────────────────

/**
 * DB_藏干_展开 — compatibility/derived view over Batch 02 DB_藏干.
 *   • `rows` is the SAME frozen array (`===`) as `DB_CANG_GAN.rows`.
 *   • Headers use Sheet43's audited labels (复合键 / 气类 / 权重) which
 *     differ from Sheet42 only in wording.
 *   • No independent 28-row store is introduced.
 */
export const DB_CANG_GAN_ZHAN_KAI = Object.freeze({
  codeName: "Sheet43",
  worksheetIndex: 43,
  sheetName: String.fromCodePoint(
    0x44, 0x42, 0x5F, 34255, 24178, 0x5F, 23637, 24320
  ), // DB_藏干_展开
  sheetNameUnicode: [68, 66, 95, 34255, 24178, 95, 23637, 24320],
  excelUsedRange: "A1:F29",
  status: "COMPATIBILITY_LAYER",
  sourceOfTruth: "M02_BATCH_02 · DB_藏干 (Sheet42, A1:F29)",
  dataEquivalenceEvidence: Object.freeze({
    columnsCompared: ["A", "B", "C", "D", "E", "F"],
    dataCellsCompared: 168,
    dataCellsMatching: 168,
    headerDifferences: Object.freeze({
      A: { sheet42: "复合键(地支_类型)", sheet43: "复合键" },
      C: { sheet42: "类型",             sheet43: "气类" },
      F: { sheet42: "权重(%)",           sheet43: "权重" },
    }),
  }),
  auditedInBatch: "M02_BATCH_06",
  recordCount: 28,
  headers: Object.freeze({
    A: HEADER_FU_HE_JIAN,   // 复合键 (Sheet43 wording)
    B: HEADER_DI_ZHI,        // 地支
    C: HEADER_QI_LEI,        // 气类 (Sheet43 wording — same tokens as Sheet42 类型)
    D: HEADER_CANG_GAN,      // 藏干
    E: HEADER_CANG_GAN_WUX,  // 藏干五行
    F: HEADER_QUAN_ZHONG,    // 权重 (Sheet43 wording — same numeric values as Sheet42 权重(%))
  }),
  /**
   * SAME frozen array as `DB_CANG_GAN.rows` — single source of truth.
   */
  rows: DB_CANG_GAN.rows,
});

// ── Compatibility lookup API (thin re-exposition of Batch 02) ────────────

function throwMissing(column, key) {
  const err = new Error(
    `[M02·DB_藏干_展开] ${column} key ${JSON.stringify(key)} not found in ` +
      "COMPATIBILITY_LAYER (single source of truth is M02 Batch02 DB_藏干). " +
      "No fuzzy match or substitution is performed."
  );
  err.code = "MISSING_REFERENCE_KEY";
  err.dataset = "DB_藏干_展开";
  err.column = column;
  err.key = key;
  throw err;
}

/**
 * Exact-match lookup by composite key `<地支>_<气类>`. Delegates to
 * Batch 02 `cangGanLookup` (same rows, same identity). Missing keys
 * throw `MISSING_REFERENCE_KEY`.
 */
export function zhanKaiLookupByCompositeKey(compositeKey) {
  if (typeof compositeKey !== "string") {
    throw new TypeError("[M02·DB_藏干_展开] compositeKey must be a string");
  }
  return cangGanLookup(compositeKey);
}

/**
 * Exact-match lookup by (地支, 气类). Delegates to Batch 02
 * `cangGanLookupBy` (same rows, same identity). Missing keys throw
 * `MISSING_REFERENCE_KEY`.
 */
export function zhanKaiLookupBy(diZhi, qiLei) {
  if (typeof diZhi !== "string" || typeof qiLei !== "string") {
    throw new TypeError(
      "[M02·DB_藏干_展开] diZhi and qiLei must both be strings"
    );
  }
  return cangGanLookupBy(diZhi, qiLei);
}

/**
 * Return all frozen rows for a given 地支 in Excel row order. Delegates
 * to Batch 02 `cangGanRowsForDiZhi`.
 */
export function zhanKaiRowsForDiZhi(diZhi) {
  if (typeof diZhi !== "string") {
    throw new TypeError("[M02·DB_藏干_展开] diZhi must be a string");
  }
  return cangGanRowsForDiZhi(diZhi);
}

/**
 * Non-throwing existence probe by composite key `<地支>_<气类>`. Used
 * by the Sheet27 B82:E84 composition helpers to skip absent 中气 /
 * 余气 segments per Excel VLOOKUP + IFERROR semantics.
 */
export function zhanKaiHasKey(compositeKey) {
  if (typeof compositeKey !== "string") return false;
  return DB_CANG_GAN_ZHAN_KAI.rows.some((r) => r.compositeKey === compositeKey);
}

// ── Sheet27 B82:E84 formula-family composers ─────────────────────────────

// Fixed audited probe order per formula: (本气, 中气, 余气).
const QI_LEI_PROBE_ORDER = Object.freeze([
  QI_LEI_TOKEN.BEN_QI,
  QI_LEI_TOKEN.ZHONG_QI,
  QI_LEI_TOKEN.YU_QI,
]);

function collectExisting(branch, project) {
  if (typeof branch !== "string" || branch.length === 0) {
    throw new TypeError(
      "[M02·B82:E84] branch must be a non-empty audited 地支 string"
    );
  }
  const parts = [];
  for (const qiLei of QI_LEI_PROBE_ORDER) {
    const key = branch + KEY_SEP + qiLei;
    if (zhanKaiHasKey(key)) {
      const row = cangGanLookup(key);
      const val = project(row);
      // Excel IFERROR skips only rows where VLOOKUP fails (i.e. key missing).
      // Present rows always contribute their audited value verbatim.
      parts.push(val);
    }
  }
  return parts.join("/");
}

/**
 * Sheet27 B82:E84 row 82 — 藏干 composition for a given branch.
 *   VLOOKUP(<branch>_本气, A:D, 4) / VLOOKUP(<branch>_中气, A:D, 4) /
 *   VLOOKUP(<branch>_余气, A:D, 4), non-empty concatenated with "/".
 *
 * @param {string} branch audited 地支 glyph (B37/B46/B54/B62 value)
 * @returns {string}
 */
export function composeSheet27Row82CangGan(branch) {
  return collectExisting(branch, (row) => row.cangGan);
}

/**
 * Sheet27 B82:E84 row 83 — 藏干五行 composition for a given branch.
 */
export function composeSheet27Row83CangGanWuXing(branch) {
  return collectExisting(branch, (row) => row.cangGanWuXing);
}

/**
 * Sheet27 B82:E84 row 84 — 权重 composition for a given branch.
 * Native Excel Double integers stringified without formatting.
 */
export function composeSheet27Row84QuanZhong(branch) {
  return collectExisting(branch, (row) => String(row.quanZhong));
}

/**
 * Combined composer for a single column of B82:E84.
 * @param {string} branch audited 地支 glyph
 * @returns {{ b82: string, b83: string, b84: string }}
 */
export function composeSheet27B82ThroughE84Column(branch) {
  return Object.freeze({
    b82: composeSheet27Row82CangGan(branch),
    b83: composeSheet27Row83CangGanWuXing(branch),
    b84: composeSheet27Row84QuanZhong(branch),
  });
}

// ── Sheet27 B90 deterministic rule ───────────────────────────────────────

/**
 * Sheet27 B90 — 空亡 flag.
 *
 * Step 1: 旬序号 = xunXuHaoBandFromB51(b51) (audited IF ladder — see
 *         M02 Batch05 `xunXuHaoBandFromB51`).
 * Step 2: (空亡支1, 空亡支2) = kongWangBranchesByXunXuHao(旬序号).
 * Step 3: If b37 === 空亡支1 OR b37 === 空亡支2 return "空亡" (audited
 *         Unicode [31354, 20129]) else "".
 *
 * @param {{ b37: string, b51: number }} input
 * @returns {string} "" | "空亡"
 */
export function computeSheet27B90({ b37, b51 }) {
  if (typeof b37 !== "string") {
    throw new TypeError("[Sheet27·B90] b37 must be a string");
  }
  const xunXuHao = xunXuHaoBandFromB51(b51); // validates b51 numericness
  const { kongWangZhi1, kongWangZhi2 } = kongWangBranchesByXunXuHao(xunXuHao);
  return b37 === kongWangZhi1 || b37 === kongWangZhi2
    ? SHEET27_B90_TRUE_OUTPUT
    : SHEET27_B90_FALSE_OUTPUT;
}
